import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { FlattenedCustomerVariantPreferencesSchema } from "../agents/returns-agents/exchange-agent/customer-variant-preferences";
import { ShippingRateTableSchema } from "../integration/redo-in-shipping/shipping-rate-table";
import { PriceSetSchema } from "../money/price-set";
import { Provider } from "../order";
import {
  AdjustmentZodSchema,
  Channel,
  PickupLocation,
  ProvisionType,
  ReturnTypeEnum,
  ReviewingParty,
  UpsellBillingTrigger,
} from "../return";
import {
  InputAnswerPartWithStepSchema,
  Payer,
  PickupPayer,
} from "../return-flow";
import {
  AddressSchema as ShipmentAddressZodSchema,
  ShipmentParcelZodSchema,
  ShippingProviderType,
} from "../shipment";
// import { GetDropoffLocationsResultSchema } from "../dropoff/dropoff-types";
import { ThirdPartyRefundProvider } from "../third-party-refunds/provider";
import { RepairFeeCollectionStrategy } from "../warranties";
import {
  AdvancedExchangeItemSchema,
  NewExchangeItemSchema,
  NonReturnableItemSchema,
  PendingReturnItemSchema,
  ReturnableItemSchema,
} from "./draft-return-items";

export enum DraftReturnSource {
  FROM_ORDER = "from_order",
  FROM_WARRANTY_REGISTRATION = "from_warranty_registration",
  FROM_DRAFT_REGISTRATION = "from_draft_registration",
  FROM_PRODUCTS = "from_products",
}

// The order is important! This is the order we will display the compensation methods in the UI
export enum CompensationMethodType {
  EXCHANGE = "exchange",
  STORE_CREDIT = "storeCredit",
  REFUND = "refund",
  REPAIR = "repair",
}

export const COMPENSATION_TYPES = Object.values(CompensationMethodType);

export const CompensationMethodSchema = z.object({
  type: createEnum(CompensationMethodType),
  // keys are pendingItemIds
  itemValues: z.record(
    z.string(),
    z.object({
      strategy: createEnum(["refund", "storeCredit", "repair"]),
      value: zExt.big(),
      exchangeCredit: zExt.big(),
      isVariantExchange: z.boolean(),
      tax: zExt.big(),
      chargeRepairFee: z.boolean().nullish(),
    }),
  ),
  totalReturnableValue: zExt.big(),
  // keys are pendingItemIds
  /** @deprecated use returnLevelAdjustments and itemLevelAdjustments instead */
  adjustments: z.record(z.string(), zExt.big()),
  returnLevelAdjustments: z.array(AdjustmentZodSchema).nullish(),
  itemLevelAdjustments: z
    .record(z.string(), z.array(AdjustmentZodSchema))
    .nullish(),
  shippingRequired: z.record(z.string(), z.boolean()),
  manualReview: z.boolean(),
  manualReviewReason: z.string().nullish(),
  flagged: z.boolean(),
  flaggedReason: z.string().nullish(),
  payer: createEnum(Payer),
  returnMethodRequired: z.boolean(),
  exchangeShippingName: z.string().nullish(),
  chargeNewOrderShipping: z.boolean(),
  discountCodeName: z
    .object({ prefix: z.string().nullish(), suffix: z.string().nullish() })
    .nullish(),
  advancedExchangeAvailable: z.boolean(),
  itemsToRefundPoints: z.array(z.string()).nullish(),
  itemsToDeductPoints: z.array(z.string()).nullish(),
  tags: z.array(z.object({ name: z.string(), color: z.string() })).nullish(),
  coverPackagePickup: z.boolean().nullish(),
});

export type CompensationMethod = z.infer<typeof CompensationMethodSchema>;
export type CompensationType = CompensationMethod["type"];

export const InteractiveMapLocationSchema = z.object({
  name: z.string(),
  address: z.object({
    address1: z.string(),
    address2: z.string().nullish(),
    city: z.string(),
    state: z.string().nullish(),
    zip: z.string(),
    country: z.string(),
  }),
  geo: z.object({ latitude: z.number(), longitude: z.number() }).nullish(),
  distance: z.string().nullish(),
  hours: z.record(z.string(), z.string()).nullish(),
});

export type InteractiveMapLocation = z.infer<
  typeof InteractiveMapLocationSchema
>;

// Enum definitions
export const ExchangeTypeContext = createEnum(["VARIANT", "ADVANCED"]);
export const PickupLocationEnum = createEnum(PickupLocation);
export const AdjustmentType = createEnum(["percentage", "amount"]);
export const ReturnOrClaimType = createEnum(["return", "claim"]);
export const ReturnRequestStatus = createEnum(["PENDING", "REJECTED"]);

// Schema definitions
const AddressSchema_ = z.object({
  name: z.string(),
  email: z.string(),
  street1: z.string(),
  street2: z.string().nullish(),
  state: z.string().nullish(),
  city: z.string(),
  country: z.string(),
  zip: z.string(),
  phone: z.string().nullish(),
  returnerName: z.string().nullish(),
  returnerEmail: z.string().nullish(),
  _id: z.string().nullish(),
});
export const AddressSchema: z.ZodType<Address> = AddressSchema_;
export interface Address extends z.infer<typeof AddressSchema_> {}

const PickupDataSchema_ = z.object({
  email: z.string().nullish(),
  phone: z.string().nullish(),
  pickupDate: z.string(),
  pickupLocation: z.object({
    packageLocation: PickupLocationEnum,
    specialInstructions: z.string().nullish(),
  }),
  textReminder: z.boolean(),
  pickupPayer: createEnum(PickupPayer).nullish(),
});
const PickupDataSchema: z.ZodType<PickupData> = PickupDataSchema_;
export interface PickupData extends z.infer<typeof PickupDataSchema_> {}

export enum ShipmentMethodName {
  PICKUP = "PICKUP",
  IN_STORE = "IN_STORE",
  SETTLEMENT = "SETTLEMENT",
  SELF_SHIP = "SELF_SHIP",
  HAPPY_RETURNS = "HAPPY_RETURNS",
  BLUE_YONDER = "BLUE_YONDER",
  RETURN_BEAR = "RETURN_BEAR",
  MANUAL_LABEL = "MANUAL_LABEL", //Merchant will create the label and send it to the customer
}

export const ShipmentMethodSchema = z.union([
  z.object({
    method: z.literal(ShipmentMethodName.PICKUP),
    pickupData: PickupDataSchema,
  }),
  z.object({ method: z.literal(ShipmentMethodName.IN_STORE) }),
  z.object({ method: z.literal(ShipmentMethodName.SETTLEMENT) }),
  z.object({ method: z.literal(ShipmentMethodName.HAPPY_RETURNS) }),
  z.object({ method: z.literal(ShipmentMethodName.BLUE_YONDER) }),
  z.object({ method: z.literal(ShipmentMethodName.RETURN_BEAR) }),
  z.object({ method: z.literal(ShipmentMethodName.SELF_SHIP) }),
  z.object({ method: z.literal(ShipmentMethodName.MANUAL_LABEL) }),
]);
export type ShipmentMethod = z.infer<typeof ShipmentMethodSchema>;

const SettlementSchema_ = z.object({
  accepted: z.boolean(),
  refund: z.number(),
});
export const SettlementSchema: z.ZodType<Settlement> = SettlementSchema_;
export interface Settlement extends z.infer<typeof SettlementSchema_> {}

const discountNameSchema_ = z.object({
  title: z.string(),
  code: z.string(),
  type: z.string().optional(),
});
export const discountNameSchema: z.ZodType<DiscountName> = discountNameSchema_;
export interface DiscountName extends z.infer<typeof discountNameSchema_> {}

const ShipmentAddressSchema_ = z.object({
  name: z.string().nullish(),
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
  country_name: z.string().nullish(),
  phone: z.string().nullish(),
  id: z.string().nullish(),
  externalLocationId: z.string().nullish(),
  trackstarWarehouseId: z.string().nullish(),
  inmarLocationId: z.string().nullish(),
  blueYonderLocationId: z.string().nullish(),
  aims360WarehouseId: z.string().nullish(),
  isSekoHub: z.boolean().nullish(),
  _id: zExt.objectId().nullish(),
  returnsCenterId: z.string().nullish(),
});
export const ShipmentAddressSchema: z.ZodType<ShipmentAddress> =
  ShipmentAddressSchema_;
export interface ShipmentAddress
  extends z.infer<typeof ShipmentAddressSchema_> {}

const HappyReturnsLocationSchema_ = z.object({
  name: z.string(),
  address: z.object({
    address: z.string(),
    city: z.string(),
    state: z.string(),
    zipcode: z.string(),
  }),
  distance: z.number(),
  lat: z.number(),
  lng: z.number(),
});
export const HappyReturnsLocationSchema: z.ZodType<HappyReturnsLocation> =
  HappyReturnsLocationSchema_;
export interface HappyReturnsLocation
  extends z.infer<typeof HappyReturnsLocationSchema_> {}

const HappyReturnsDetailsSchema_ = z.object({
  price: zExt.big(),
  locations: z.array(HappyReturnsLocationSchema),
  zipCode: z.string(),
  retailerId: z.string(),
});
export const HappyReturnsDetailsSchema: z.ZodType<HappyReturnsDetails> =
  HappyReturnsDetailsSchema_;
export interface HappyReturnsDetails
  extends z.infer<typeof HappyReturnsDetailsSchema_> {}

const BlueYonderLocationSchema_ = z.object({
  blueyonderLocationId: z.string(),
  locationName: z.string(),
  address: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  }),
  distance: z.string(), // e.g., "1.2 miles"
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
});

export const BlueYonderLocationSchema: z.ZodType<BlueYonderLocation> =
  BlueYonderLocationSchema_;
export interface BlueYonderLocation
  extends z.infer<typeof BlueYonderLocationSchema_> {}

const BlueYonderDetailsSchema_ = z.object({
  locations: z.array(BlueYonderLocationSchema),
  retailerCompanyId: z.string(),
});
export const BlueYonderDetailsSchema: z.ZodType<BlueYonderDetails> =
  BlueYonderDetailsSchema_;
export interface BlueYonderDetails
  extends z.infer<typeof BlueYonderDetailsSchema_> {}

const DropoffLocationSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  address: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  }),
  distance: z.string().describe("e.g., '1.2 miles', MUST BE IN MILES"), // e.g., "1.2 miles"
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
});

export const DropoffLocationSchema: z.ZodType<DropoffLocation> =
  DropoffLocationSchema_;
export interface DropoffLocation
  extends z.infer<typeof DropoffLocationSchema_> {}

const ReturnBearDetailsSchema_ = z.object({
  returnBearCaseId: z.string(),
  returnBearCaseShortId: z.string(),
  returnBearExternalRMAReferenceId: z.string().nullish(),
  returnBearCaseVersion: z.number().nullish(),
  locations: z.array(DropoffLocationSchema),
  shippingLabelAvailability: z.boolean(),
  flatRateFee: z.number().describe("The flat rate fee plus markup"),
  rate: z
    .number()
    .describe(
      "The default rate for the route from the shopper's country to the merchant's country determined by Return Bear",
    ),
  markup: z
    .number()
    .nullable()
    .describe(
      "The markup for the route from the shopper's country to the merchant's country is determined by Redo",
    ),
});

export const ReturnBearDetailsSchema: z.ZodType<ReturnBearDetails> =
  ReturnBearDetailsSchema_;
export interface ReturnBearDetails
  extends z.infer<typeof ReturnBearDetailsSchema_> {}

const ParcelDimsSchema_ = z.object({
  lengthInInches: z.number().nullish(),
  widthInInches: z.number().nullish(),
  heightInInches: z.number().nullish(),
});
export const ParcelDimsSchema: z.ZodType<ParcelDims> = ParcelDimsSchema_;
export interface ParcelDims extends z.infer<typeof ParcelDimsSchema_> {}

const ParcelProductSchema_ = z.object({
  grams: z.number().nullish(),
  quantity: z.number().nullish(),
  dims: ParcelDimsSchema.nullish(),
});
export const ParcelProductSchema: z.ZodType<ParcelProduct> =
  ParcelProductSchema_;
export interface ParcelProduct extends z.infer<typeof ParcelProductSchema_> {}

const CorrectedProductSchema_ = z.object({
  variantId: z.string().nullish(),
  productId: z.string().nullish(),
  grams: z.number().nullish(),
  dims: ParcelDimsSchema.nullish(),
});
export const CorrectedProductSchema: z.ZodType<CorrectedProduct> =
  CorrectedProductSchema_;
export interface CorrectedProduct
  extends z.infer<typeof CorrectedProductSchema_> {}

const DraftReturnShipmentSchema_ = z.object({
  carrier: z.string(),
  fee: z.string(),
  flatRateFee: z.string().nullish(),
  pickupFee: z.string().nullish(),
  pickupIneligibility: z.string().nullish(),
  pickupCoveredByMerchant: z.boolean().nullish(),
  address: ShipmentAddressSchema,
  pendingReturnItems: z.array(z.string()),
  happyReturnsDetails: HappyReturnsDetailsSchema.nullish(),
  blueYonderDetails: BlueYonderDetailsSchema.nullish(),
  returnBearDetails: ReturnBearDetailsSchema.nullish(),
  // dropoffs: z.array(GetDropoffLocationsResultSchema).default([]),
  inStoreLocations: z.array(InteractiveMapLocationSchema).nullish(),
  shippingProviderRate: z.string().nullish(),
  merchantUpcharge: z.string().nullish(),
  /** The label upcharge/downcharge applied by merchant settings (flat or percentage) */
  merchantAppliedUpcharge: z.string().nullish(),
  customerUpchargeFee: z.number().nullish(),
  marketRate: z.string().nullish(),
  calculatedMarketRate: z
    .object({
      marketRate: z.string(),
      carrier: z.string(),
      serviceLevel: z.string(),
      listedRate: z.string(),
      defaultDiscountPercent: z.number(),
      teamDiscountPercent: z.number(),
    })
    .nullish(),
  /**
   * Insurance cost for the shipment, calculated based on product prices.
   * This is charged to customers when shippingInsurance is enabled on the team.
   * Redo accepts liability for insurance claims rather than purchasing carrier insurance.
   */
  insuranceCost: z.number().nullish(),
  rateSource: z
    .object({
      isMerchantCarrier: z.boolean().nullish(),
      shippingProviderType: createEnum(ShippingProviderType).nullish(),
      isFromAlternativeAccount: z.boolean().nullish(),
    })
    .nullish(),
  shipmentId: z.string().nullish(),
  shipmentParcel: ShipmentParcelZodSchema.nullish(),
  customerAddress: ShipmentAddressZodSchema.nullish(),
  parcelProduct: ParcelProductSchema.nullish(),
  correctedProducts: z.array(CorrectedProductSchema).nullish(),
  estimatedAt: z.date().nullish(),
});
export const DraftReturnShipmentSchema: z.ZodType<DraftReturnShipment> =
  DraftReturnShipmentSchema_;
export interface DraftReturnShipment
  extends z.infer<typeof DraftReturnShipmentSchema_> {}

const PriceRangeSchema = z.tuple([z.number(), z.number()]);
export type PriceRange = z.infer<typeof PriceRangeSchema>;

const ReturnTotalCalculationsSchema_ = z.object({
  pickupAccept: z.boolean(),
  pickupFee: z.number().nullish(),
  inStoreAccept: z.boolean(),
  settlementAccept: z.boolean(),
  settlementRefund: z.number(),
  shippingFee: z.number(),
  customerUpchargeFee: z.number().optional(),
  exchangeShippingFee: z.number(),
  totalShippingFee: z.number(),
  labelDeducted: z.boolean(),
  canDeductLabel: z.boolean().nullish(),
  returnValue: z.number(),
  totalTaxes: z.number(),
  newOrderValue: z.number(),
  totalNewOrderTaxes: z.number(),
  totalAdjustmentValue: z.number(),
  totalReturnValue: z.number(),
  totalStoreCredit: z.number(),
  totalRefund: z.number(),
  totalRepairFee: PriceRangeSchema,
  hasGreenReturnItems: z.boolean(),
  hasNonGreenReturnItems: z.boolean(),
  showNewItems: z.boolean(),
  showNewOrderAddress: z.boolean(),
  paymentOwed: z.number(),
  totalPaymentOwed: z.number(),
  paymentOwedRange: PriceRangeSchema.nullish(),
  skipPayment: z.boolean(),
  totalLoyaltyPointsDeducted: z.number(),
  totalLoyaltyPointsRefunded: z.number(),
  totalReturnValueDeductedDueToLoyaltyPoints: z.number(),
  totalOrderShipping: z.number(),
  newOrderDiscount: z.number().nullish(),
});
const ReturnTotalCalculationsSchema: z.ZodType<ReturnTotalCalculations> =
  ReturnTotalCalculationsSchema_;
export interface ReturnTotalCalculations
  extends z.infer<typeof ReturnTotalCalculationsSchema_> {}

const ReturnTotalCalculationsOutputSchema_ = z.object({
  instant: ReturnTotalCalculationsSchema,
  processed: ReturnTotalCalculationsSchema,
});
export const ReturnTotalCalculationsOutputSchema: z.ZodType<ReturnTotalCalculationsOutput> =
  ReturnTotalCalculationsOutputSchema_;
export interface ReturnTotalCalculationsOutput
  extends z.infer<typeof ReturnTotalCalculationsOutputSchema_> {}

const LineItemSchema_ = z.object({ id: z.string(), exchangeCount: z.number() });
const LineItemSchema: z.ZodType<LineItem> = LineItemSchema_;
export interface LineItem extends z.infer<typeof LineItemSchema_> {}

const OrderSchema_ = z.object({
  id: zExt.objectId(),
  warrantyId: zExt.objectId().nullish(),
  name: z.string(),
  tags: z.array(z.string()),
  orderDate: z.date(),
  totalPrice: z.string(),
  currency: z.string(),
  protected: z.boolean(),
  packageProtected: z.boolean(),
  finalSaleReturnsProtected: z.boolean(),
  productsCoveredByFinalSale: z.array(z.string()),
  paymentMethods: z.array(z.string()),
  salesChannel: z.string(),
  exchangeCount: z.number().nullish(),
  discounts: z.array(discountNameSchema),
  platform: createEnum(Provider),
  sourceNotes: z
    .union([z.string(), z.array(InputAnswerPartWithStepSchema)])
    .nullish(),
  coverage: z.object({
    exchange: z.boolean(),
    storeCredit: z.boolean(),
    refund: z.boolean(),
    repair: z.boolean(),
  }),
  lineItems: z.array(LineItemSchema).nullish(),
});
const OrderSchema: z.ZodType<Order> = OrderSchema_;
export interface Order extends z.infer<typeof OrderSchema_> {}

const NewOrderDataSchema_ = z.object({
  items: z.array(AdvancedExchangeItemSchema),
  /** @deprecated use totalPriceSet instead */
  totalPrice: z.string(), // without tax
  totalPriceSet: PriceSetSchema.nullish(),
  /** @deprecated use totalTaxSet instead */
  totalTax: z.string(),
  totalTaxSet: PriceSetSchema.nullish(),
  taxLineInfo: z
    .array(z.object({ title: z.string(), rate: z.number() }))
    .nullish(),
  discountCodes: z.array(z.string()),
  discountAllocations: z.array(
    z.object({
      /** @deprecated use discountedAmountSet instead */
      discountedAmount: z.object({
        amount: z.string(),
        currencyCode: z.string(),
      }),
      discountedAmountSet: PriceSetSchema.nullish(),
    }),
  ),
  blockAutomaticDiscounts: z.boolean().nullish(),
});
export const NewOrderDataSchema: z.ZodType<NewOrderData> = NewOrderDataSchema_;
export interface NewOrderData extends z.infer<typeof NewOrderDataSchema_> {}

// Main ReturnRequest schema
const DraftReturnSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  treatmentHash: z.string(),
  flow: z.any(),
  finalizeFlow: z.any(),
  locationFlow: z.any().nullish(),
  labelFlow: z.any().nullish(),
  returnType: createEnum(ReturnTypeEnum),
  orders: z.array(OrderSchema), // I wish this was a Record<string, OrderSchema>. I wish mongoose supported this in a way that wasn't a map. plz lmk if you know how to do this
  customer: z.object({
    id: z.string().nullish(),
    shippingAddress: AddressSchema.nullish(),
    name: z.string(),
    tags: z.array(z.string()),
    numCustomerClaims: z.number(),
    numCustomerReturns: z.number(),
    numOrders: z.number(),
    // Add dates for time-based conditions
    orderDates: z.array(z.date()).nullish(),
    returnDates: z.array(z.date()).nullish(),
    returnRate: z.number(),
    newOrderAddress: AddressSchema.nullish(),
    timezone: z.string().nullish(),
    // This is an email that is set by the merchant during merchant created returns when the customer does not have an email on file.
    updatedEmail: z.string().nullish(),
    variantPreferences: FlattenedCustomerVariantPreferencesSchema.nullish(),
    redoCustomerId: z.string().nullish(),
  }),
  featureFlags: z.record(z.string(), z.boolean()).nullish(),
  newOrderItems: z.array(NewExchangeItemSchema),
  newOrderData: NewOrderDataSchema.nullish(),
  returnableItems: z.array(ReturnableItemSchema),
  nonReturnableItems: z.array(NonReturnableItemSchema).default([]),
  pendingReturnItems: z.array(PendingReturnItemSchema),
  compensationMethods: z.array(CompensationMethodSchema),
  thirdPartyRefundProvider: createEnum(ThirdPartyRefundProvider).nullish(),
  selectedMethod: createEnum(CompensationMethodType).nullish(),
  shipmentMethod: ShipmentMethodSchema.nullish(),
  shipments: z.array(DraftReturnShipmentSchema).nullish(),
  submitted: z.boolean().nullish(),
  rejected: z.boolean().nullish(),
  provisionType: createEnum(ProvisionType),
  currency: createEnum(Currency).nullish(),
  presentmentCurrency: createEnum(Currency).nullish(),
  pickupVariant: z.string().nullish(),
  referralId: z.string().nullish(),
  merchantCreated: z.boolean().nullish(),
  merchantName: z.string().nullish(),
  merchantRoles: z.array(z.string()).nullish(),
  repairFeeCollectionStrategy: createEnum(
    RepairFeeCollectionStrategy,
  ).nullish(),
  tnplContractId: zExt.objectId().nullish(),
  upsellBillingTrigger: createEnum(UpsellBillingTrigger).nullish(),
  provider: createEnum(Provider).nullish(),
  channel: createEnum(Channel).nullish(),
  pos: z.object({ locationId: z.string() }).nullish(),
  exchangeRate: z.number().optional(),
  selectedExchangeShippingOption: z
    .object({
      rateTable: z.lazy(() => ShippingRateTableSchema),
      price: z.number(),
      currency: createEnum(Currency),
      name: z.string(), // Display name from the rate table
    })
    .nullish(),
  source: createEnum(DraftReturnSource).nullish(),
  newItemToExchangeRate: z.number().optional(),
  returnTags: z
    .array(z.object({ name: z.string(), color: z.string() }))
    .optional(),
  reviewingParty: createEnum(ReviewingParty).nullish(),
  isGiftReturn: z.boolean().nullish(),
});
export const DraftReturnSchema: z.ZodType<DraftReturn> = DraftReturnSchema_;
export interface DraftReturn extends z.infer<typeof DraftReturnSchema_> {}

export type ReturnRequest = DraftReturn;

/**
 * If the draft return is going to have one or more items being exchanged.
 */
export function isDraftingExchangeReturn(draftReturn: DraftReturn): boolean {
  return (
    draftReturn.pendingReturnItems.some(
      (item) =>
        !!item.variantExchangeItem ||
        !!item.exchangeGroup ||
        (item.advancedExchangeItems?.length ?? 0) > 0,
    ) || (draftReturn.newOrderData?.items.length ?? 0) > 0
  );
}
